/**
 * Production-safe logging utility
 * Prevents sensitive data from being logged in production
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Keywords that indicate sensitive data
const SENSITIVE_KEYWORDS = [
  'token', 'password', 'key', 'secret', 'auth', 'bearer',
  'session_uuid', 'access_token', 'refresh_token', 'api_key',
  'authorization', 'credentials', 'session', 'jwt', 'email',
  'user_id', 'customer_id', 'profile', 'data', 'response',
  'uuid', 'id', 'phone', 'address'
];

/**
 * Check if data contains sensitive information
 */
const containsSensitiveData = (data) => {
  if (typeof data === 'string') {
    return SENSITIVE_KEYWORDS.some(keyword => 
      data.toLowerCase().includes(keyword)
    );
  }
  
  if (typeof data === 'object' && data !== null) {
    return Object.keys(data).some(key => 
      SENSITIVE_KEYWORDS.some(keyword => 
        key.toLowerCase().includes(keyword)
      )
    );
  }
  
  return false;
};

/**
 * Sanitize sensitive data for logging
 */
const sanitize = (data) => {
  if (typeof data === 'string' && containsSensitiveData(data)) {
    return '[REDACTED]';
  }
  
  if (typeof data === 'object' && data !== null) {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      const isSensitiveKey = SENSITIVE_KEYWORDS.some(keyword => 
        key.toLowerCase().includes(keyword)
      );
      
      if (isSensitiveKey) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = sanitize(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }
  
  return data;
};

/**
 * Safe logger that only logs in development and sanitizes sensitive data
 */
const logger = {
  log: (message, ...args) => {
    if (isDevelopment) {
      console.log(message, ...args.map(sanitize));
    }
  },
  
  info: (message, ...args) => {
    if (isDevelopment) {
      console.info(message, ...args.map(sanitize));
    }
  },
  
  warn: (message, ...args) => {
    if (isDevelopment) {
      console.warn(message, ...args.map(sanitize));
    }
  },
  
  error: (message, ...args) => {
    console.error(message, ...args.map(sanitize));
  },
  
  debug: (message, ...args) => {
    if (isDevelopment) {
      console.debug(message, ...args.map(sanitize));
    }
  }
};

// Export individual functions for easier bulk replacement
export const log = logger.log;
export const info = logger.info;
export const warn = logger.warn;
export const error = logger.error;
export const debug = logger.debug;

export default logger;